import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, FileText, Mail, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusLabels = { draft: "Brouillon", sent: "En attente", accepted: "Accepté", rejected: "Refusé" };
const statusColors = {
  draft: "bg-slate-100 text-slate-600", sent: "bg-blue-50 text-blue-600",
  accepted: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-600"
};

export default function PortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState("view"); // "view" | "code_sent" | "verifying"
  const [verificationCode, setVerificationCode] = useState("");
  const [codeEmail, setCodeEmail] = useState("");
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    const fetchDevis = async () => {
      try {
        const res = await fetch(`${API}/portal/devis/${token}`);
        if (res.ok) {
          const result = await res.json();
          setData(result);
          if (result.viewer) setViewer(result.viewer);
        }
        else setError("Devis introuvable ou lien expiré");
      } catch { setError("Erreur de connexion"); }
      finally { setLoading(false); }
    };
    fetchDevis();
  }, [token]);

  const handleRequestCode = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/portal/devis/${token}/request-code`, { method: "POST" });
      const result = await res.json();
      if (res.ok) {
        toast.success(result.message);
        setCodeEmail(result.email_sent_to || data?.devis?.client_email || "");
        setStep("code_sent");
      } else {
        toast.error(result.detail || "Erreur");
      }
    } catch { toast.error("Erreur de connexion"); }
    finally { setProcessing(false); }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error("Veuillez saisir le code à 6 chiffres");
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/portal/devis/${token}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: verificationCode })
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(result.message);
        setStep("view");
        setData(prev => ({ ...prev, devis: { ...prev.devis, status: "accepted" } }));
      } else {
        toast.error(result.detail || "Code incorrect");
      }
    } catch { toast.error("Erreur de connexion"); }
    finally { setProcessing(false); }
  };

  const handleReject = async () => {
    if (!window.confirm("Confirmez-vous le refus de ce devis ?")) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API}/portal/devis/${token}/reject`, { method: "POST" });
      const result = await res.json();
      if (res.ok) { toast.success(result.message); setData(prev => ({ ...prev, devis: { ...prev.devis, status: "rejected" } })); }
      else toast.error(result.detail || "Erreur");
    } finally { setProcessing(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Card className="max-w-md w-full border-slate-200"><CardContent className="p-8 text-center">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Devis introuvable</h2>
        <p className="text-sm text-slate-500">{error}</p>
      </CardContent></Card>
    </div>
  );

  const { devis, company } = data;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "-";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-page">
      <Toaster position="top-right" />

      <main className="max-w-4xl mx-auto px-6 py-8">
        <Card className="border-slate-200 overflow-hidden">
          {/* Blue Header */}
          <div className="bg-[#1a3a5c] text-white p-8">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <img src="/jvc-logo.png" alt="JVC" className="w-20 h-20 rounded-lg object-contain bg-white p-1" />
                <div>
                  <h1 className="font-barlow text-2xl font-bold uppercase tracking-tight">{company?.company_name || "SAS J.V.C"}</h1>
                  <p className="text-blue-200 text-sm mt-0.5">{company?.activities || "Structure métallique, Soudure industrielle, Serrurerie"}</p>
                  <p className="text-blue-300 text-xs mt-2">{company?.address}, {company?.postal_code} {company?.city}</p>
                  <p className="text-blue-300 text-xs">Tel: {company?.phone} | Mobile: {company?.mobile}</p>
                  <p className="text-blue-300 text-xs">Email: {company?.email}</p>
                </div>
              </div>
              <div className="text-right">
                <h2 className="font-barlow text-3xl font-bold uppercase">DEVIS</h2>
                <p className="text-blue-200 text-lg font-semibold mt-1">{devis.devis_number}</p>
              </div>
            </div>
          </div>

          <CardContent className="p-8">
            {/* Client info + Meta */}
            <div className="grid grid-cols-2 gap-8 mb-6">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Client</p>
                <p className="font-semibold text-slate-900 text-lg">{devis.client_name}</p>
                <p className="text-sm text-slate-500">{devis.client_email}</p>
                {devis.client_address && <p className="text-sm text-slate-500">{devis.client_address}</p>}
                {devis.client_city && <p className="text-sm text-slate-500">{devis.client_postal_code} {devis.client_city}</p>}
                {devis.client_siret && <p className="text-sm text-slate-400">SIRET: {devis.client_siret}</p>}
              </div>
              <div className="text-right space-y-2">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase">Date</p>
                  <p className="text-sm font-medium">{formatDate(devis.created_at)}</p>
                </div>
                {devis.valid_until && <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase">Valable jusqu'au</p>
                  <p className="text-sm font-medium">{formatDate(devis.valid_until)}</p>
                </div>}
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase">Statut</p>
                  <Badge className={`${statusColors[devis.status]} text-sm font-medium border-0 px-3 py-1`} data-testid="devis-status-badge">
                    {statusLabels[devis.status]}
                  </Badge>
                </div>
              </div>
            </div>

            {devis.objet && (
              <div className="mb-6 p-3 bg-blue-50 rounded-md border border-blue-100">
                <p className="text-xs font-semibold text-blue-500 uppercase tracking-wider">Objet</p>
                <p className="text-sm font-medium text-slate-900 mt-0.5">{devis.objet}</p>
              </div>
            )}

            {/* Items table */}
            <div className="border rounded-lg overflow-hidden mb-6">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#1a3a5c]">
                    <TableHead className="text-white text-xs font-semibold">Description</TableHead>
                    <TableHead className="text-white text-xs font-semibold text-center">Qté</TableHead>
                    <TableHead className="text-white text-xs font-semibold text-right">Prix unitaire</TableHead>
                    <TableHead className="text-white text-xs font-semibold text-right">Montant net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devis.items?.map((item, idx) => (
                    item.item_type === "title" ? (
                      <TableRow key={idx} className="bg-[#1a3a5c]">
                        <TableCell colSpan={4} className="text-white font-bold text-sm uppercase py-2.5">{item.description}</TableCell>
                      </TableRow>
                    ) : item.item_type === "forfait" ? (
                      <TableRow key={idx} className="bg-amber-50">
                        <TableCell colSpan={4} className="text-sm text-amber-900 italic py-2.5 whitespace-pre-line">{item.description}</TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <TableCell className="text-sm">{item.description}</TableCell>
                        <TableCell className="text-sm text-center">{item.quantity} {item.unit}</TableCell>
                        <TableCell className="text-sm text-right">{item.unit_price?.toFixed(2)} EUR</TableCell>
                        <TableCell className="text-sm text-right font-medium">{item.total?.toFixed(2)} EUR</TableCell>
                      </TableRow>
                    )
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-6">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm py-1">
                  <span className="text-slate-500">Total net</span>
                  <span className="font-medium">{devis.subtotal?.toFixed(2)} EUR</span>
                </div>
                {devis.tax_rate > 0 && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-slate-500">TVA ({devis.tax_rate}%)</span>
                    <span className="font-medium">{devis.tax_amount?.toFixed(2)} EUR</span>
                  </div>
                )}
                <div className="bg-[#1a3a5c] text-white rounded-md p-3 flex justify-between items-center">
                  <span className="font-semibold text-sm">NET A PAYER</span>
                  <span className="text-2xl font-bold font-barlow">{devis.total?.toFixed(2)} EUR</span>
                </div>
              </div>
            </div>

            {/* Payment terms - conditional */}
            {devis.show_payment_terms !== false && (
              <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-md mb-6">
                <p className="text-sm font-semibold text-amber-800">Conditions de paiement</p>
                <p className="text-xs text-amber-700 mt-1">Un acompte de 50% du montant total est exigé à la validation du devis et BC pour le démarrage des travaux. Le solde est dû à la réception de la facture.</p>
              </div>
            )}

            {devis.notes && (
              <div className="p-4 bg-slate-50 rounded-md mb-6">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Notes & Conditions</p>
                <p className="text-sm text-slate-600">{devis.notes}</p>
              </div>
            )}

            {/* Bank details */}
            <div className="p-4 bg-slate-50 rounded-md mb-6 text-sm">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Coordonnées bancaires</p>
              <div className="grid grid-cols-2 gap-2">
                <p className="text-slate-600"><span className="font-medium text-slate-700">IBAN:</span> {company?.iban}</p>
                <p className="text-slate-600"><span className="font-medium text-slate-700">BIC:</span> {company?.bic}</p>
              </div>
            </div>

            {/* Accept / Reject buttons - Step 1: Request Code */}
            {(devis.status === "sent" || devis.status === "draft") && step === "view" && (
              <>
                <Separator className="my-6" />
                <div className="flex items-center justify-center gap-4">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 h-12" onClick={handleRequestCode} disabled={processing} data-testid="accept-devis-btn">
                    <ShieldCheck className="w-5 h-5 mr-2" /> {processing ? "Envoi du code..." : "Accepter le devis"}
                  </Button>
                  <Button size="lg" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 px-8 h-12" onClick={handleReject} disabled={processing} data-testid="reject-devis-btn">
                    <X className="w-5 h-5 mr-2" /> Refuser
                  </Button>
                </div>
              </>
            )}

            {/* Step 2: Verification Code Entry */}
            {(devis.status === "sent" || devis.status === "draft") && step === "code_sent" && (
              <>
                <Separator className="my-6" />
                <div className="max-w-md mx-auto" data-testid="verification-step">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                    <Mail className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">Code de vérification envoyé</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Un code à 6 chiffres a été envoyé à <strong>{codeEmail}</strong>.
                      <br />Saisissez-le ci-dessous pour confirmer l'acceptation du devis.
                    </p>
                    <div className="flex items-center gap-3 justify-center mb-4">
                      <Input
                        type="text"
                        maxLength={6}
                        value={verificationCode}
                        onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                        placeholder="000000"
                        className="w-40 text-center text-2xl tracking-[0.3em] font-bold h-14 border-2 border-blue-300 focus:border-blue-500"
                        data-testid="verification-code-input"
                      />
                    </div>
                    <div className="flex gap-3 justify-center">
                      <Button onClick={handleVerifyCode} disabled={processing || verificationCode.length < 6} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6" data-testid="verify-code-btn">
                        <Check className="w-4 h-4 mr-2" /> {processing ? "Vérification..." : "Valider le code"}
                      </Button>
                      <Button variant="outline" onClick={() => { setStep("view"); setVerificationCode(""); }} className="text-slate-600" data-testid="back-btn">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Retour
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400 mt-4">Ce code est valable 15 minutes. En validant, vous acceptez le devis et le bon de commande associé.</p>
                  </div>
                </div>
              </>
            )}

            {devis.status === "accepted" && (
              <div className="p-6 bg-emerald-50 rounded-lg text-center">
                <Check className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-lg font-semibold text-emerald-700">Devis accepté et vérifié</p>
                {devis.accepted_by_name && (
                  <p className="text-sm text-emerald-600 mt-1 font-medium">
                    Accepté par {devis.accepted_by_name}{devis.accepted_by_email ? ` (${devis.accepted_by_email})` : ""}
                    {devis.verified_at ? ` le ${formatDate(devis.verified_at)}` : ""}
                  </p>
                )}
                <p className="text-sm text-emerald-600 mt-1">Un bon de commande a été créé et envoyé par e-mail. L'équipe JVC va prendre contact avec vous.</p>
              </div>
            )}
            {devis.status === "rejected" && (
              <div className="p-6 bg-red-50 rounded-lg text-center">
                <X className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-lg font-semibold text-red-600">Devis refusé</p>
                <p className="text-sm text-red-500 mt-1">N'hésitez pas à nous contacter pour en discuter.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <footer className="text-center py-6">
          <p className="text-xs text-slate-400">{company?.company_name || "SAS J.V.C"} - SIRET: {company?.siret} | {company?.address}, {company?.postal_code} {company?.city}</p>
        </footer>
      </main>
    </div>
  );
}
